import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { CircularProgress, Modal } from "@mui/material";
import PrimaryButton from "../../common/ui/PrimaryButton";
import SecondaryButton from "../../common/ui/SecondaryButton";
import useTheme from "../../common/hooks/useTheme";
import {
  AiSummaryMetric,
  AiSummaryResponse,
  AiSummarySystem,
  AiSummaryTakeawayItem,
} from "./aiSummaryTypes";
import AiSummaryMeasurementTable from "./AiSummaryMeasurementTable";
import AiAmrSummaryView from "./AiAmrSummaryView";
import AiProgressSummaryView from "./AiProgressSummaryView";
import AiBloodProgressSummaryView from "./AiBloodProgressSummaryView";
import { AiBloodProgressSummary } from "./aiBloodProgressSummaryResponse";
import { AiProgressSummary } from "./aiProgressSummaryTypes";
import { AiAmrSummary } from "./aiAmrSummaryTypes";
import AiRmrSummaryView from "./AiRmrSummaryView";
import AiRmrInBodySummaryView from "./AiRmrInBodySummaryView";
import AiSummaryRecommendationsView, {
  hasAiSummaryRecommendations,
} from "./AiSummaryRecommendationsView";
import { AiRmrSummary } from "./aiRmrSummaryTypes";
import { AiRmrInBodySummary } from "./aiRmrInBodySummaryTypes";
import { AiSummaryKind, getAiSummaryOption } from "./aiSummaryKinds";
import { exportAiSummaryToPdf } from "./aiSummaryPdf";
import AiSummaryPdfDocument from "./AiSummaryPdfDocument";
import classes from "./AiSummary.module.css";

type Props = {
  open: boolean;
  isLoading: boolean;
  summaryKind: AiSummaryKind;
  isNotImplemented: boolean;
  hasNoData: boolean;
  error: string | null;
  healthSummary: AiSummaryResponse | null;
  rmrSummary: AiRmrSummary | null;
  rmrInBodySummary: AiRmrInBodySummary | null;
  amrSummary: AiAmrSummary | null;
  progressSummary?: AiProgressSummary | null;
  bloodProgressSummary?: AiBloodProgressSummary | null;
  onClose: () => void;
  onRetry: () => void;
};

const formatGroupLabel = (value: string) => {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const hasSystemItems = (systems: AiSummarySystem[]) =>
  systems.some(
    (system) =>
      system.score !== undefined ||
      Boolean(system.scoreCategory) ||
      system.highlights.length > 0 ||
      system.lowlights.length > 0
  );

const MetricCard = ({
  metric,
  tone,
}: {
  metric: AiSummaryMetric;
  tone: "positive" | "attention";
}) => (
  <article
    className={`${classes.metricCard} ${
      tone === "positive"
        ? classes.metricCardPositive
        : classes.metricCardAttention
    }`}
  >
    <div className={classes.metricCardHeader}>
      <h5>{metric.title}</h5>
    </div>
    {metric.scorePercent !== undefined && (
      <div
        className={classes.metricScoreTrack}
        role="progressbar"
        aria-label={`${metric.name} score`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={metric.scorePercent}
      >
        <span style={{ width: `${metric.scorePercent}%` }} />
      </div>
    )}
    {metric.bullets.length > 0 && (
      <ul>
        {metric.bullets.map((bullet, bulletIndex) => (
          <li key={`${metric.name}-bullet-${bulletIndex}`}>{bullet}</li>
        ))}
      </ul>
    )}
    <AiSummaryMeasurementTable metric={metric} />
  </article>
);

const MetricCollection = ({
  title,
  items,
  tone,
}: {
  title: string;
  items: AiSummaryMetric[];
  tone: "positive" | "attention";
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classes.metricCollection}>
      <p
        className={`${classes.metricCollectionTitle} ${
          tone === "positive"
            ? classes.metricCollectionTitlePositive
            : classes.metricCollectionTitleAttention
        }`}
      >
        {title}
      </p>
      <div className={classes.metricGrid}>
        {items.map((metric, index) => (
          <MetricCard
            key={`${metric.name}-${index}`}
            metric={metric}
            tone={tone}
          />
        ))}
      </div>
    </div>
  );
};

const MetricSystems = ({
  systems,
  tone,
}: {
  systems: AiSummarySystem[];
  tone: "positive" | "attention";
}) => {
  if (systems.length === 0) {
    return <p className={classes.emptyText}>No items were returned.</p>;
  }

  return (
    <div className={classes.metricSystems}>
      {systems.map((system, index) => (
        <section
          key={`${system.name}-${index}`}
          className={`${classes.systemCard} ${
            tone === "positive"
              ? classes.systemCardPositive
              : classes.systemCardAttention
          }`}
        >
          <div className={classes.systemHeader}>
            <h4>{formatGroupLabel(system.name)}</h4>
            {(system.score !== undefined || system.scoreCategory) && (
              <span className={classes.systemScore}>
                {system.score !== undefined && (
                  <>
                    System score <strong>{system.score}</strong>
                  </>
                )}
                {system.scoreCategory && (
                  <span className={classes.systemScoreCategory}>
                    {system.scoreCategory}
                  </span>
                )}
              </span>
            )}
          </div>
          {system.score !== undefined && (
            <div
              className={classes.systemScoreTrack}
              role="progressbar"
              aria-label={`${system.name} system score`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={system.score}
            >
              <span style={{ width: `${system.score}%` }} />
            </div>
          )}
          <MetricCollection
            title="Highlights"
            items={system.highlights}
            tone="positive"
          />
          <MetricCollection
            title="Needs attention"
            items={system.lowlights}
            tone="attention"
          />
        </section>
      ))}
    </div>
  );
};

const TakeawaySummary = ({
  title,
  items,
  tone,
}: {
  title: string;
  items: AiSummaryTakeawayItem[];
  tone: "positive" | "attention";
}) => (
  <article
    className={
      tone === "positive" ? classes.takeawayPositive : classes.takeawayAttention
    }
  >
    <h4>{title}</h4>
    <div className={classes.takeawaySummaryList}>
      {items.map((item, index) => (
        <div
          key={`${tone}-${item.title}-${index}`}
          className={classes.takeawaySummaryItem}
        >
          {item.title && <h5>{item.title}</h5>}
          {item.text && <p>{item.text}</p>}
          {item.points && item.points.length > 0 && (
            <ul className={classes.takeawayPoints}>
              {item.points.map((point, pointIndex) => (
                <li key={`${item.title}-point-${pointIndex}`}>{point}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  </article>
);

const AiSummaryModal = ({
  open,
  isLoading,
  summaryKind,
  isNotImplemented,
  hasNoData,
  error,
  healthSummary,
  rmrSummary,
  rmrInBodySummary,
  amrSummary,
  progressSummary,
  bloodProgressSummary,
  onClose,
  onRetry,
}: Props) => {
  const theme = useTheme();
  const pdfDocumentRef = useRef<HTMLDivElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const summaryOption = getAiSummaryOption(summaryKind);
  const summary = healthSummary;
  const pdfBackgroundColor = theme.colors_ds.NEUTRAL_BG_MAIN;
  const cssVariables = {
    "--ai-summary-surface": theme.colors_ds.NEUTRAL_BG_MAIN,
    "--ai-summary-surface-subtle": theme.colorTokens.N[30],
    "--ai-summary-surface-raised": theme.colorTokens.N[20],
    "--ai-summary-border": theme.colors_ds.NEUTRAL_BORDER_WEAK,
    "--ai-summary-border-strong": theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    "--ai-summary-text-strong": theme.colors_ds.NEUTRAL_TEXT_STRONG,
    "--ai-summary-text": theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    "--ai-summary-text-weak": theme.colors_ds.NEUTRAL_TEXT_WEAK,
    "--ai-summary-primary": theme.colors_ds.PRIMARY_TEXT_MAIN,
    "--ai-summary-primary-soft": theme.colorTokens.P[10],
    "--ai-summary-primary-border": theme.colorTokens.P[20],
    "--ai-summary-success": theme.colorTokens.S[60],
    "--ai-summary-success-soft": theme.colorTokens.S[10],
    "--ai-summary-success-border": theme.colorTokens.S[20],
    "--ai-summary-danger": theme.colorTokens.D[60],
    "--ai-summary-danger-soft": theme.colorTokens.D[10],
    "--ai-summary-danger-border": theme.colorTokens.D[20],
    "--ai-summary-warning-soft": theme.colorTokens.W[10],
    "--ai-summary-warning-border": theme.colorTokens.W[30],
    "--ai-summary-warning": theme.colorTokens.W[70],
    "--ai-summary-neutral-accent": theme.colorTokens.N[70],
  } as React.CSSProperties;
  const hasTakeaway = Boolean(
    summary &&
      (summary.keyTakeaway.overallPicture ||
        summary.keyTakeaway.highlightsSummary.length > 0 ||
        summary.keyTakeaway.lowlightsSummary.length > 0)
  );
  const hasHighlights = Boolean(summary && hasSystemItems(summary.highlights));
  const hasLowlights = Boolean(summary && hasSystemItems(summary.lowlights));
  const hasRecommendations = Boolean(
    summary &&
      hasAiSummaryRecommendations({
        trainingPlan: summary.trainingPlan,
        nutrition: summary.nutrition,
        recommendations: summary.recommendations,
      })
  );
  const visibleSectionKeys = [
    ...(hasTakeaway ? ["takeaway"] : []),
    ...(hasHighlights ? ["highlights"] : []),
    ...(hasLowlights ? ["lowlights"] : []),
    ...(hasRecommendations ? ["recommendations"] : []),
  ];
  const getSectionNumber = (key: string) =>
    String(visibleSectionKeys.indexOf(key) + 1);
  const hasSummaryContent = Boolean(
    visibleSectionKeys.length > 0 || summary?.disclaimer
  );
  const canExport = Boolean(
    !isLoading &&
      !error &&
      !isNotImplemented &&
      !hasNoData &&
      ((summaryKind === "health_overview" && summary) ||
        (summaryKind === "rmr_summary" && rmrSummary) ||
        (summaryKind === "rmr_inbody_summary" && rmrInBodySummary) ||
        (summaryKind === "progress_summary_1" && progressSummary) ||
        (summaryKind === "progress_summary_2" && bloodProgressSummary) ||
        (summaryKind === "amr_summary" && amrSummary))
  );
  const handleExport = useCallback(async () => {
    if (!pdfDocumentRef.current || isExporting) {
      return;
    }

    setExportError(null);
    setIsExporting(true);
    try {
      await exportAiSummaryToPdf(
        pdfDocumentRef.current,
        summaryKind,
        pdfBackgroundColor
      );
    } catch (exportFailure) {
      console.error("AI Summary PDF export failed", exportFailure);
      setExportError("The PDF could not be generated. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, pdfBackgroundColor, summaryKind]);

  useEffect(() => {
    setExportError(null);
  }, [
    amrSummary,
    healthSummary,
    rmrInBodySummary,
    rmrSummary,
    progressSummary,
    bloodProgressSummary,
    summaryKind,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="ai-summary-title"
      sx={{ zIndex: 1600 }}
      BackdropProps={{
        sx: { backgroundColor: theme.colors_ds.NEUTRAL_BG_OVERLAY },
      }}
    >
      <div
        className={`${classes.modal} ${
          isLoading || isNotImplemented || hasNoData ? classes.loadingModal : ""
        }`}
        style={cssVariables}
      >
        <header className={classes.header}>
          <div className={classes.headerIdentity}>
            <span className={classes.headerIcon} aria-hidden="true">
              <SummarizeOutlinedIcon fontSize="small" />
            </span>
            <div>
              <h2 id="ai-summary-title">AI Summary</h2>
              <p className={classes.eyebrow}>
                {summaryOption.kind === "health_overview"
                  ? "AI-generated health overview"
                  : summaryOption.label}
              </p>
            </div>
          </div>
          <div className={classes.headerActions}>
            {canExport && (
              <SecondaryButton
                label={isExporting ? "Exporting..." : "Export PDF"}
                icon={
                  isExporting ? (
                    <CircularProgress
                      size={15}
                      color="inherit"
                      aria-hidden="true"
                    />
                  ) : (
                    <PictureAsPdfOutlinedIcon
                      fontSize="small"
                      aria-hidden="true"
                    />
                  )
                }
                isDisable={isExporting}
                onClick={() => void handleExport()}
                sx={{ flexShrink: 0, justifyContent: "center" }}
              />
            )}
            <SecondaryButton
              label=""
              icon={<CloseIcon fontSize="small" />}
              aria-label="Close AI Summary"
              onClick={onClose}
              sx={{
                width: "38px",
                minWidth: "38px",
                padding: 0,
                flexShrink: 0,
                justifyContent: "center",
              }}
            />
          </div>
        </header>

        <main className={classes.content}>
          {exportError && (
            <div className={classes.exportError} role="alert">
              {exportError}
            </div>
          )}
          {isLoading && (
            <div className={classes.loadingState} role="status">
              <div className={classes.stateCard}>
                <span className={classes.loadingIndicator} aria-hidden="true">
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }}
                  />
                </span>
                <div>
                  <h3>Creating the {summaryOption.label.toLowerCase()}</h3>
                  <p>Reviewing the selected client's available health data.</p>
                  <p>
                    Depending on the customer's available data, this process may
                    take a few minutes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !error && !isNotImplemented && hasNoData && (
            <div
              className={classes.loadingState}
              role="status"
              aria-label="No customer data found"
            >
              <div className={`${classes.stateCard} ${classes.noDataCard}`}>
                <span className={classes.loadingIndicator} aria-hidden="true">
                  <InfoOutlinedIcon fontSize="medium" />
                </span>
                <div>
                  <h3>No health data found</h3>
                  <p>
                    No data was found for the selected customer, so the{" "}
                    {summaryOption.label.toLowerCase()} could not be created.
                  </p>
                  <p>
                    Once health data is available for this customer, you can
                    request the summary again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !error && isNotImplemented && (
            <div className={classes.loadingState} role="status">
              <div className={classes.stateCard}>
                <span className={classes.loadingIndicator} aria-hidden="true">
                  <HourglassEmptyOutlinedIcon fontSize="medium" />
                </span>
                <div>
                  <h3>{summaryOption.label} is not implemented yet</h3>
                  <p>
                    This summary type is planned but is not available in the
                    current version.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && error && (
            <div className={classes.errorState} role="alert">
              <div className={`${classes.stateCard} ${classes.errorCard}`}>
                <div>
                  <h3>We couldn't create the AI Summary</h3>
                  <p>{error}</p>
                </div>
                <PrimaryButton label="Try again" onClick={onRetry} />
              </div>
            </div>
          )}

          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "health_overview" &&
            summary && (
              <div className={classes.summaryBody}>
                {!hasSummaryContent && (
                  <section className={classes.emptySummaryState}>
                    <h3>No summary content was returned</h3>
                    <p>
                      The AI response did not include any displayable health
                      overview fields. You can close this window and try again.
                    </p>
                  </section>
                )}

                {hasTakeaway && (
                  <section className={classes.section}>
                    <div className={classes.sectionHeading}>
                      <span>{getSectionNumber("takeaway")}</span>
                      <h3>Key Takeaway</h3>
                    </div>
                    {summary.keyTakeaway.overallPicture && (
                      <div className={classes.takeawayHero}>
                        <span>Overall picture</span>
                        <p>{summary.keyTakeaway.overallPicture}</p>
                      </div>
                    )}
                    {(summary.keyTakeaway.highlightsSummary.length > 0 ||
                      summary.keyTakeaway.lowlightsSummary.length > 0) && (
                      <div
                        className={`${classes.takeawayGrid} ${
                          summary.keyTakeaway.highlightsSummary.length === 0 ||
                          summary.keyTakeaway.lowlightsSummary.length === 0
                            ? classes.takeawayGridSingle
                            : ""
                        }`}
                      >
                        {summary.keyTakeaway.highlightsSummary.length > 0 && (
                          <TakeawaySummary
                            title="What's working well"
                            items={summary.keyTakeaway.highlightsSummary}
                            tone="positive"
                          />
                        )}
                        {summary.keyTakeaway.lowlightsSummary.length > 0 && (
                          <TakeawaySummary
                            title="What needs attention"
                            items={summary.keyTakeaway.lowlightsSummary}
                            tone="attention"
                          />
                        )}
                      </div>
                    )}
                  </section>
                )}

                {hasHighlights && (
                  <section className={classes.section}>
                    <div className={classes.sectionHeading}>
                      <span>{getSectionNumber("highlights")}</span>
                      <h3>Highlights</h3>
                    </div>
                    <MetricSystems
                      systems={summary.highlights}
                      tone="positive"
                    />
                  </section>
                )}

                {hasLowlights && (
                  <section className={classes.section}>
                    <div className={classes.sectionHeading}>
                      <span>{getSectionNumber("lowlights")}</span>
                      <h3>Lowlights</h3>
                    </div>
                    <MetricSystems
                      systems={summary.lowlights}
                      tone="attention"
                    />
                  </section>
                )}

                {hasRecommendations && (
                  <section className={classes.section}>
                    <div className={classes.sectionHeading}>
                      <span>{getSectionNumber("recommendations")}</span>
                      <h3>Recommendations</h3>
                    </div>
                    <AiSummaryRecommendationsView
                      trainingPlan={summary.trainingPlan}
                      nutrition={summary.nutrition}
                      recommendations={summary.recommendations}
                    />
                  </section>
                )}

                {summary.disclaimer && (
                  <section className={classes.disclaimer}>
                    <InfoOutlinedIcon aria-hidden="true" />
                    <div>
                      <h3>Disclaimer</h3>
                      <p>{summary.disclaimer}</p>
                    </div>
                  </section>
                )}
              </div>
            )}

          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "rmr_summary" &&
            rmrSummary && (
              <div>
                <AiRmrSummaryView summary={rmrSummary} />
              </div>
            )}

          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "rmr_inbody_summary" &&
            rmrInBodySummary && (
              <div>
                <AiRmrInBodySummaryView summary={rmrInBodySummary} />
              </div>
            )}

          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "amr_summary" &&
            amrSummary && (
              <div>
                <AiAmrSummaryView summary={amrSummary} />
              </div>
            )}
          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "progress_summary_1" &&
            progressSummary && (
              <AiProgressSummaryView summary={progressSummary} />
            )}
          {!isLoading &&
            !error &&
            !isNotImplemented &&
            !hasNoData &&
            summaryKind === "progress_summary_2" &&
            bloodProgressSummary && (
              <AiBloodProgressSummaryView summary={bloodProgressSummary} />
            )}
        </main>
        {canExport &&
          createPortal(
            <AiSummaryPdfDocument
              ref={pdfDocumentRef}
              kind={summaryKind}
              healthSummary={healthSummary}
              rmrSummary={rmrSummary}
              rmrInBodySummary={rmrInBodySummary}
              amrSummary={amrSummary}
              progressSummary={progressSummary}
              bloodProgressSummary={bloodProgressSummary}
              style={cssVariables}
            />,
            document.body
          )}
      </div>
    </Modal>
  );
};

export default AiSummaryModal;
